import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Clock, User, MapPin, Truck, AlertTriangle, Send } from 'lucide-react';
import { useAuth } from './AuthContext';
import api from './services/api';
import './VehicleInspection.css';
import { getDepartmentDisplayName } from './utils/departmentLabels';
import { isDemoMode } from './utils/runtimeMode';

const COMMERCIAL_DRIVER_HOME_TERMINAL = '240 Sunset Blvd NE, Town of Diamond Valley, T0L 0H0';
const COMMERCIAL_DRIVER_INSPECTION_TYPE = 'Alberta Carrier - Schedule 1 Truck, Tractor, Trailer Inspection';

const hasCaoDepartmentEditAccess = (currentUser, authUser) => {
  const email = String(currentUser?.email || currentUser?.username || authUser?.email || authUser?.username || '').trim().toLowerCase();
  return email === 'cao@diamondvalley.town' || (isDemoMode() && ['3p3cao@gmail.com', 'ctdemo.cao@proton.me'].includes(email));
};

const VehicleInspection = () => {
  // Get current user from auth context
  const { user } = useAuth();
  
  // State management
  const [currentUser, setCurrentUser] = useState(null);
  
  const [formData, setFormData] = useState({
    inspectorName: '', // Will be set from current user
    department: '', // Will be set from current user
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().split(' ')[0].substring(0, 5),
    shiftStart: '08:00',
    shiftEnd: '16:00',
    vehicleType: '',
    vehicleDescription: '',
    plate: '',
    currentKms: '',
    lastOilChange: '',
    kmsToNextOilChange: '',
    isCommercialDriver: false,
    commercialEndTime: ''
  });

  // Track whether the Department select was manually edited (so we don't overwrite a deliberate change)
  const [departmentTouched, setDepartmentTouched] = useState(false);

  const [inspectionItems, setInspectionItems] = useState([]);
  const [deficiencies, setDeficiencies] = useState([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [equipmentChecklists, setEquipmentChecklists] = useState({});
  const [isLoadingChecklists, setIsLoadingChecklists] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});

  // Department-specific data
  const departments = useMemo(() => ['Roads', 'Utilities', 'Parks', 'Recreation', 'Administration'], []);

  // Equipment types we don't want to show in dropdown menus (legacy values may still exist)
  const hiddenEquipmentTypes = useMemo(() => new Set(['Sheep River Shuttle']), []);
  
  // Map user department names to valid department options
  const mapUserDepartment = useCallback((userDept) => {
    if (userDept === undefined || userDept === null) return 'Administration'; // Default fallback

    const deptLower = String(userDept).trim().toLowerCase();
    if (!deptLower) return 'Administration';
    
    // Direct matches
    if (departments.some(dept => dept.toLowerCase() === deptLower)) {
      return departments.find(dept => dept.toLowerCase() === deptLower);
    }
    
    // Partial matches for common variations
    if (deptLower.includes('road')) return 'Roads';
    if (deptLower.includes('utility') || deptLower.includes('utilities')) return 'Utilities';
    if (deptLower.includes('park')) return 'Parks';
    if (deptLower.includes('recreation') || deptLower.includes('rec')) return 'Recreation';
    if (deptLower.includes('admin') || deptLower.includes('office') || deptLower.includes('management')) return 'Administration';
    
    // Default fallback
    return 'Administration';
  }, [departments]);

  const extractDepartmentName = useCallback((profileLike) => {
    if (!profileLike || typeof profileLike !== 'object') return '';
    const raw =
      profileLike.department ||
      profileLike.DEPARTMENT ||
      profileLike.departmentName ||
      profileLike.DEPARTMENT_NAME ||
      profileLike.DEPARTMENTNAME ||
      profileLike.department_name ||
      '';
    return String(raw || '').trim();
  }, []);

  const extractDisplayName = useCallback((profileLike) => {
    if (!profileLike || typeof profileLike !== 'object') return '';
    const firstName = profileLike.firstName || profileLike.FIRST_NAME || '';
    const lastName = profileLike.lastName || profileLike.LAST_NAME || '';
    const fullName = `${String(firstName || '').trim()} ${String(lastName || '').trim()}`.trim();
    return fullName || String(profileLike.name || profileLike.email || profileLike.username || '').trim();
  }, []);
  
  const equipmentByDepartment = useMemo(() => ({
    'Roads': ['Grader', 'Heavy Truck Eqt > 1 Ton', 'Light Truck Car SUV', 'Street Sweeper'],
    'Utilities': ['Backhoe', 'Heavy Truck Eqt > 1 Ton', 'Light Truck Car SUV', 'Loader'],
    'Parks': ['Club Car', 'Gas Powered General Eqpt', 'Kubota RTV', 'Kubota Zero Turn', 'Light Truck Car SUV', 'Toolcat', 'Toro', 'Willie'],
    'Recreation': ['Forklift', 'Ice Resurfacer', 'Light Truck Car SUV', 'Sheep River Shuttle'],
    'Administration': ['Heavy Truck Eqt > 1 Ton', 'Light Truck Car SUV']
  }), []);

  // Friendly boolean for roads department (handles case/whitespace variations)
  const isRoads = React.useMemo(() => String(formData.department || '').toLowerCase().includes('road'), [formData.department]);

  // Debugging: show exact department string when rendering
  React.useEffect(() => {
    console.log('VehicleInspection: department check ->', JSON.stringify(formData.department), 'isRoads=', isRoads);
  }, [formData.department, isRoads]);

  // All equipment types available across departments
  const allEquipmentTypes = useMemo(() => [
    'Backhoe',
    'Club Car',
    'Forklift',
    'Gas Powered General Eqpt',
    'Genie Dynapac',
    'Grader',
    'Heavy Truck Eqt > 1 Ton',
    'Ice Resurfacer',
    'Kubota RTV',
    'Kubota Zero Turn',
    'Light Truck Car SUV',
    'Loader',
    'Skidsteer',
    'Street Sweeper',
    'Toolcat',
    'Toro',
    'Willie'
  ], []);

  const availableEquipmentOptions = useMemo(() => {
    const departmentEquipment = equipmentByDepartment[formData.department] || [];

    return Array.from(
      new Set([
        ...departmentEquipment,
        ...allEquipmentTypes
      ])
    )
      .filter((equipment) => !hiddenEquipmentTypes.has(equipment))
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
  }, [allEquipmentTypes, equipmentByDepartment, formData.department, hiddenEquipmentTypes]);

  // Load equipment-specific checklists from Excel file
  useEffect(() => {
    const loadChecklistsFromExcel = async () => {
      try {
        setIsLoadingChecklists(true);
        
        console.log('🔄 Using default checklists (Excel loading disabled in development)');
        
        // Use default checklists since Excel file serving has issues in development
        setEquipmentChecklists(getDefaultChecklists());
        setIsLoadingChecklists(false);
        
      } catch (error) {
        console.error('Error loading checklists:', error);
        // Fallback to default checklist
        setEquipmentChecklists(getDefaultChecklists());
        setIsLoadingChecklists(false);
      }
    };
    
    loadChecklistsFromExcel();
  }, []);

  // Fallback default checklists if Excel loading fails
  const getDefaultChecklists = () => {
    const createChecklistVariant = ({ baseChecklist = [], prefix, labelOverrides = {}, appendedItems = [] }) => {
      const normalizedPrefix = String(prefix || 'equipment').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
      const buildItemId = (value, fallbackIndex = 0) => {
        const normalizedValue = String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');

        return `${normalizedPrefix}_${normalizedValue || `item_${fallbackIndex + 1}`}`;
      };

      return [
        ...baseChecklist.map((item, index) => {
          const originalLabel = String(item.label || '').trim();
          const nextLabel = labelOverrides[originalLabel] || item.label;
          const baseIdSegment = String(item.id || '')
            .replace(/^[^_]+_/, '')
            .trim();

          return {
            ...item,
            id: buildItemId(baseIdSegment || nextLabel, index),
            label: nextLabel
          };
        }),
        ...appendedItems.map((label, index) => ({
          id: buildItemId(label, baseChecklist.length + index),
          label,
          category: 'General Inspection'
        }))
      ];
    };

    const defaultChecklists = {
    'Sheep River Shuttle': [
      { id: 'sheep_hoses', label: 'Hoses', category: 'Fan Belt' },
      { id: 'sheep_wires', label: 'Wires', category: 'Fan Belt' },
      { id: 'sheep_windshield_mirrors', label: 'Windshield, Mirrors, Windows', category: 'Exterior Inspection' },
      { id: 'sheep_battery', label: 'Battery', category: 'General Outside Appearance' },
      { id: 'sheep_seats_floor', label: 'Seats, Floor, Housekeeping', category: 'Interior Inspection' },
      { id: 'sheep_salt_sand', label: 'Salt/Sand/Cleaning Products', category: 'Interior Inspection' },
      { id: 'sheep_brakes_height', label: 'Brakes (Height and Feel)', category: 'Windshield Wipers' },
      { id: 'sheep_clearance_lights', label: 'Clearance Lights/Running Lights', category: 'Turn Signals & Flashers' },
      { id: 'sheep_on_road', label: 'On the Road', category: 'Brake Lights & Taillights' },
      { id: 'sheep_brakes_parking', label: 'Brakes (Height and Feel)', category: 'Parking Brake' },
      { id: 'sheep_transmission', label: 'Transmission', category: 'Parking Brake' },
      { id: 'sheep_suspension', label: 'Suspension', category: 'Engine Start Up' },
      { id: 'sheep_startup', label: 'Engine Start Up', category: 'Engine Start Up' }
    ],
    'Heavy Truck Eqt > 1 Ton': [
      { id: 'heavy_trained_certified', label: 'Trained/Certified to use Equipment', category: 'General Inspection' },
      { id: 'heavy_ppe', label: 'PPE', category: 'General Inspection' },
      { id: 'heavy_placards', label: 'Placards/Signage', category: 'General Inspection' },
      { id: 'heavy_power_steering', label: 'Power Steering', category: 'General Inspection' },
      { id: 'heavy_fuel_level', label: 'Fuel Level', category: 'General Inspection' },
      { id: 'heavy_visible_leaks', label: 'Visible Leaks', category: 'General Inspection' },
      { id: 'heavy_exhaust', label: 'Exhaust System', category: 'General Inspection' },
      { id: 'heavy_engine_oil', label: 'Engine Oil Level', category: 'General Inspection' },
      { id: 'heavy_coolant', label: 'Coolant Level', category: 'General Inspection' },
      { id: 'heavy_steering_fluid', label: 'Steering Fluid Level', category: 'General Inspection' },
      { id: 'heavy_warning_lights', label: 'Are there any Warning Lights', category: 'General Inspection' },
      { id: 'heavy_headlights', label: 'Headlights', category: 'General Inspection' },
      { id: 'heavy_parking_lights', label: 'Parking Lights', category: 'General Inspection' },
      { id: 'heavy_tail_lights', label: 'Tail Lights', category: 'General Inspection' },
      { id: 'heavy_brake_lights', label: 'Brake Lights', category: 'General Inspection' },
      { id: 'heavy_directional_signals', label: 'Directional Signals', category: 'General Inspection' },
      { id: 'heavy_hazard_lights', label: 'Hazard Lights', category: 'General Inspection' },
      { id: 'heavy_reverse_lights', label: 'Reverse Lights', category: 'General Inspection' },
      { id: 'heavy_tire_visual', label: 'Tire Visual', category: 'General Inspection' },
      { id: 'heavy_tire_tread', label: 'Tire Tread Greater than 50%', category: 'General Inspection' },
      { id: 'heavy_rims', label: 'Rims', category: 'General Inspection' },
      { id: 'heavy_windshield', label: 'Windshield Condition', category: 'General Inspection' },
      { id: 'heavy_booster_cables', label: 'Booster Cables', category: 'General Inspection' },
      { id: 'heavy_fire_extinguisher', label: 'Fire Extinguisher (Certified in last year)', category: 'General Inspection' },
      { id: 'heavy_first_aid', label: 'First Aid Kit', category: 'General Inspection' },
      { id: 'heavy_flashlight', label: 'Flashlight', category: 'General Inspection' },
      { id: 'heavy_damage_exterior', label: 'Any Notable Damage Exterior', category: 'General Inspection' },
      { id: 'heavy_damage_interior', label: 'Any Notable Damage Interior', category: 'General Inspection' },
      { id: 'heavy_defrosters', label: 'Defrosters/Heaters/AC', category: 'General Inspection' },
      { id: 'heavy_hitch', label: 'Hitch/Pin/5th Wheel', category: 'General Inspection' },
      { id: 'heavy_insurance', label: 'verify insurance/Registration/License', category: 'General Inspection' },
      { id: 'heavy_attachments', label: 'Attachments are secure', category: 'General Inspection' },
      { id: 'heavy_water_pump', label: 'Water Pump', category: 'General Inspection' },
      { id: 'heavy_plow', label: 'Plow', category: 'General Inspection' },
      { id: 'heavy_sander', label: 'Sander', category: 'General Inspection' },
      { id: 'heavy_hydraulic_hose', label: 'Hydraulic Hose/Fittings', category: 'General Inspection' },
      { id: 'heavy_attachment_switches', label: 'Attachment Switches are functional', category: 'General Inspection' },
      { id: 'heavy_cargo', label: 'Cargo Securement', category: 'General Inspection' }
    ],
    'Light Truck Car SUV': [
      { id: 'light_power_steering', label: 'Power Steering', category: 'General Inspection' },
      { id: 'light_fuel_level', label: 'Fuel Level', category: 'General Inspection' },
      { id: 'light_visible_leaks', label: 'Visible Leaks', category: 'General Inspection' },
      { id: 'light_exhaust', label: 'Exhaust System', category: 'General Inspection' },
      { id: 'light_engine_oil', label: 'Engine Oil Level', category: 'General Inspection' },
      { id: 'light_coolant', label: 'Coolant Level', category: 'General Inspection' },
      { id: 'light_washer_fluid', label: 'Washer Fluid', category: 'General Inspection' },
      { id: 'light_steering_fluid', label: 'Steering Fluid Level', category: 'General Inspection' },
      { id: 'light_warning_lights', label: 'Are there any Warning Lights', category: 'General Inspection' },
      { id: 'light_headlights', label: 'Headlights', category: 'General Inspection' },
      { id: 'light_parking_lights', label: 'Parking Lights', category: 'General Inspection' },
      { id: 'light_tail_lights', label: 'Tail Lights', category: 'General Inspection' },
      { id: 'light_brake_lights', label: 'Brake Lights', category: 'General Inspection' },
      { id: 'light_directional_signals', label: 'Directional Signals', category: 'General Inspection' },
      { id: 'light_hazard_lights', label: 'Hazard Lights', category: 'General Inspection' },
      { id: 'light_reverse_lights', label: 'Reverse Lights', category: 'General Inspection' },
      { id: 'light_tire_visual', label: 'Tire Visual', category: 'General Inspection' },
      { id: 'light_tire_tread', label: 'Tire Tread Greater than 50%', category: 'General Inspection' },
      { id: 'light_rims', label: 'Rims', category: 'General Inspection' },
      { id: 'light_spare_tire', label: 'Spare Tire', category: 'General Inspection' },
      { id: 'light_windshield', label: 'Windshield Condition', category: 'General Inspection' },
      { id: 'light_wipers', label: 'Windshield Wipers', category: 'General Inspection' },
      { id: 'light_booster_cables', label: 'Booster Cables', category: 'General Inspection' },
      { id: 'light_fire_extinguisher', label: 'Fire Extinguisher (Certified in last year)', category: 'General Inspection' },
      { id: 'light_first_aid', label: 'First Aid Kit', category: 'General Inspection' },
      { id: 'light_flashlight', label: 'Flashlight', category: 'General Inspection' },
      { id: 'light_hi_vis', label: 'Hi-Vis Clothing', category: 'General Inspection' },
      { id: 'light_tire_jack', label: 'Tire Jack & Wheel Wrench', category: 'General Inspection' },
      { id: 'light_damage_exterior', label: 'Any Notable Damage Exterior', category: 'General Inspection' },
      { id: 'light_damage_interior', label: 'Any Notable Damage Interior', category: 'General Inspection' },
      { id: 'light_lights_clean', label: 'Lights and Reflectors Clean', category: 'General Inspection' },
      { id: 'light_wash_clean', label: 'Wash & Clean Vehicle Weekly', category: 'General Inspection' },
      { id: 'light_defrosters', label: 'Defrosters/Heaters/AC', category: 'General Inspection' },
      { id: 'light_hitch', label: 'Hitch & Pin', category: 'General Inspection' },
      { id: 'light_ice_scraper', label: 'Ice Scraper', category: 'General Inspection' }
    ],
    'Grader': [
      { id: 'grader_trained_certified', label: 'Trained/Certified to use equipment', category: 'General Inspection' },
      { id: 'grader_cab_components', label: 'Cab Compenents', category: 'General Inspection' },
      { id: 'grader_cab_structure', label: 'Cab Structure (ROPS)', category: 'General Inspection' },
      { id: 'grader_mirrors', label: 'Mirrors', category: 'General Inspection' },
      { id: 'grader_handholds', label: 'Holdholds (cab & Frame)', category: 'General Inspection' },
      { id: 'grader_steps', label: 'Steps', category: 'General Inspection' },
      { id: 'grader_fenders', label: 'Fenders/Guarding', category: 'General Inspection' },
      { id: 'grader_fan_shroud', label: 'Fan Shroud & Guards', category: 'General Inspection' },
      { id: 'grader_cutting_edge', label: 'Cutting Edge', category: 'General Inspection' },
      { id: 'grader_articulation_lock', label: 'Articulation Lock', category: 'General Inspection' },
      { id: 'grader_power_steering', label: 'Power Steering', category: 'General Inspection' },
      { id: 'grader_fuel_level', label: 'Fuel Level', category: 'General Inspection' },
      { id: 'grader_visible_leaks', label: 'Visible Leaks', category: 'General Inspection' },
      { id: 'grader_exhaust', label: 'Exhaust System', category: 'General Inspection' },
      { id: 'grader_engine_oil', label: 'Engine Oil Level', category: 'General Inspection' },
      { id: 'grader_coolant', label: 'Coolant Level', category: 'General Inspection' },
      { id: 'grader_hydraulic_level', label: 'Hydraulic Level', category: 'General Inspection' },
      { id: 'grader_washer_fluid', label: 'Washer Fluid', category: 'General Inspection' },
      { id: 'grader_steering_fluid', label: 'Steering Fluid Level', category: 'General Inspection' },
      { id: 'grader_warning_lights', label: 'Are there any Warning Lights', category: 'General Inspection' },
      { id: 'grader_headlights', label: 'Headlights', category: 'General Inspection' },
      { id: 'grader_parking_lights', label: 'Parking Lights', category: 'General Inspection' },
      { id: 'grader_tail_lights', label: 'Tail Lights', category: 'General Inspection' },
      { id: 'grader_brake_lights', label: 'Brake Lights', category: 'General Inspection' },
      { id: 'grader_turn_signals', label: 'Turn Signals', category: 'General Inspection' },
      { id: 'grader_hazard_lights', label: 'Hazard Lights', category: 'General Inspection' },
      { id: 'grader_reverse_lights', label: 'Reverse Lights', category: 'General Inspection' },
      { id: 'grader_lights_clean', label: 'Lights and Reflectors Clean', category: 'General Inspection' },
      { id: 'grader_tires_rims', label: 'Tires/Rims Visual', category: 'General Inspection' },
      { id: 'grader_tire_tread', label: 'Tire Tread Greater than 50%', category: 'General Inspection' },
      { id: 'grader_windshield', label: 'Windshield Condition', category: 'General Inspection' },
      { id: 'grader_wipers', label: 'Windshield Wipers', category: 'General Inspection' },
      { id: 'grader_fire_extinguisher', label: 'Fire Extinguisher (Certified in last year)', category: 'General Inspection' },
      { id: 'grader_first_aid', label: 'First Aid Kit', category: 'General Inspection' },
      { id: 'grader_hi_vis', label: 'Hi-Vis clothing', category: 'General Inspection' },
      { id: 'grader_damage_exterior', label: 'Any Notable Damage Exterior', category: 'General Inspection' },
      { id: 'grader_damage_interior', label: 'Any Notable Damage Interior', category: 'General Inspection' },
      { id: 'grader_defrosters', label: 'Defrosters/Heaters/AC', category: 'General Inspection' }
    ],
    'Loader': [
      { id: 'loader_trained_certified', label: 'Trained/Certified to use equipment', category: 'General Inspection' },
      { id: 'loader_wheels', label: 'Wheels - Front and Rear', category: 'General Inspection' },
      { id: 'loader_cab_structure', label: 'Cab Structure (ROPS)', category: 'General Inspection' },
      { id: 'loader_air_inlet', label: 'Air Inlet Cover', category: 'General Inspection' },
      { id: 'loader_radiator', label: 'Radiator and Coolers', category: 'General Inspection' },
      { id: 'loader_coolant', label: 'Coolant Level', category: 'General Inspection' },
      { id: 'loader_engine_oil', label: 'Engine Oil Level', category: 'General Inspection' },
      { id: 'loader_air_cleaner', label: 'Air Cleaner', category: 'General Inspection' },
      { id: 'loader_hydraulic_fluid', label: 'Hydrualic Fluid', category: 'General Inspection' },
      { id: 'loader_cab_air_filter', label: 'Cab Air Filter', category: 'General Inspection' },
      { id: 'loader_transmission_oil', label: 'Transmission Oil Level', category: 'General Inspection' },
      { id: 'loader_electrical_wires', label: 'Electrical Wires', category: 'General Inspection' },
      { id: 'loader_boom_bucket', label: 'Boom Bucket Sheet Metal', category: 'General Inspection' },
      { id: 'loader_cutting_edges', label: 'Cutting Edges', category: 'General Inspection' },
      { id: 'loader_hardware', label: 'Hardware', category: 'General Inspection' },
      { id: 'loader_park_brake', label: 'Park Brake', category: 'General Inspection' },
      { id: 'loader_hydraulic_system', label: 'Hydraulic System', category: 'General Inspection' },
      { id: 'loader_guarding', label: 'Gaurding', category: 'General Inspection' },
      { id: 'loader_seatbelt', label: 'Seatbelt', category: 'General Inspection' },
      { id: 'loader_brakes', label: 'Brakes', category: 'General Inspection' },
      { id: 'loader_backup_alarm', label: 'Back up Alarm', category: 'General Inspection' },
      { id: 'loader_first_aid', label: 'First-aid Kit', category: 'General Inspection' },
      { id: 'loader_fire_extinguisher', label: 'Fire Extinguisher (Has it been Certified in last year?)', category: 'General Inspection' }
    ],
    'Forklift': [
      { id: 'forklift_pre_start_visual', label: 'Pre-start visual inspection', category: 'General Inspection' },
      { id: 'forklift_daily_checklist', label: 'Complete daily inspection checklist', category: 'General Inspection' },
      { id: 'forklift_fluid_levels', label: 'Check all fluid levels (oil, hydraulic, coolant)', category: 'General Inspection' },
      { id: 'forklift_battery_charge', label: 'Battery charge level (if electric)', category: 'General Inspection' },
      { id: 'forklift_fuel_level', label: 'Fuel level (if gas/diesel)', category: 'General Inspection' },
      { id: 'forklift_tires_condition', label: 'Tires condition and pressure', category: 'General Inspection' },
      { id: 'forklift_forks_condition', label: 'Forks condition and attachment', category: 'General Inspection' },
      { id: 'forklift_mast_operation', label: 'Mast operation (lift, lower, tilt)', category: 'General Inspection' },
      { id: 'forklift_hydraulic_system', label: 'Hydraulic system operation', category: 'General Inspection' },
      { id: 'forklift_engine_performance', label: 'Engine performance', category: 'General Inspection' },
      { id: 'forklift_brakes_operation', label: 'Service and parking brakes', category: 'General Inspection' },
      { id: 'forklift_steering_response', label: 'Steering response', category: 'General Inspection' },
      { id: 'forklift_controls_operation', label: 'All controls operation', category: 'General Inspection' },
      { id: 'forklift_lights_function', label: 'Lights function (headlights, warning)', category: 'General Inspection' },
      { id: 'forklift_horn_function', label: 'Horn function', category: 'General Inspection' },
      { id: 'forklift_backup_alarm', label: 'Backup alarm function', category: 'General Inspection' },
      { id: 'forklift_warning_devices', label: 'Warning devices and strobes', category: 'General Inspection' },
      { id: 'forklift_seat_condition', label: 'Operator seat and restraints', category: 'General Inspection' },
      { id: 'forklift_overhead_guard', label: 'Overhead guard condition', category: 'General Inspection' },
      { id: 'forklift_load_backrest', label: 'Load backrest condition', category: 'General Inspection' },
      { id: 'forklift_mirrors_clean', label: 'Mirrors clean and properly adjusted', category: 'General Inspection' },
      { id: 'forklift_fire_extinguisher', label: 'Fire extinguisher present and charged', category: 'General Inspection' },
      { id: 'forklift_capacity_plate', label: 'Load capacity plate visible and legible', category: 'General Inspection' },
      { id: 'forklift_fuel_leaks', label: 'Check for fuel or fluid leaks', category: 'General Inspection' },
      { id: 'forklift_structural_damage', label: 'Check for structural damage', category: 'General Inspection' },
      { id: 'forklift_operational_test', label: 'Complete operational test', category: 'General Inspection' }
    ],
    'Backhoe': [
      { id: 'backhoe_trained_certified', label: 'Trained/Certified to use equipment', category: 'General Inspection' },
      { id: 'backhoe_cab_components', label: 'Cab Components', category: 'General Inspection' },
      { id: 'backhoe_cab_structure', label: 'Cab Structure (ROPS)', category: 'General Inspection' },
      { id: 'backhoe_mirrors', label: 'Mirrors', category: 'General Inspection' },
      { id: 'backhoe_handholds', label: 'Handholds (cab & Frame)', category: 'General Inspection' },
      { id: 'backhoe_steps', label: 'Steps', category: 'General Inspection' },
      { id: 'backhoe_fenders', label: 'Fenders/Guarding', category: 'General Inspection' },
      { id: 'backhoe_fan_shroud', label: 'Fan Shroud & Guards', category: 'General Inspection' },
      { id: 'backhoe_bucket_condition', label: 'Bucket Condition', category: 'General Inspection' },
      { id: 'backhoe_stabilizers', label: 'Stabilizers/Outriggers', category: 'General Inspection' },
      { id: 'backhoe_steering', label: 'Steering', category: 'General Inspection' },
      { id: 'backhoe_fuel_level', label: 'Fuel Level', category: 'General Inspection' },
      { id: 'backhoe_visible_leaks', label: 'Visible Leaks', category: 'General Inspection' },
      { id: 'backhoe_exhaust', label: 'Exhaust System', category: 'General Inspection' },
      { id: 'backhoe_engine_oil', label: 'Engine Oil Level', category: 'General Inspection' },
      { id: 'backhoe_coolant', label: 'Coolant Level', category: 'General Inspection' },
      { id: 'backhoe_hydraulic_level', label: 'Hydraulic Level', category: 'General Inspection' },
      { id: 'backhoe_washer_fluid', label: 'Washer Fluid', category: 'General Inspection' },
      { id: 'backhoe_warning_lights', label: 'Are there any Warning Lights', category: 'General Inspection' },
      { id: 'backhoe_headlights', label: 'Headlights', category: 'General Inspection' },
      { id: 'backhoe_parking_lights', label: 'Parking Lights', category: 'General Inspection' },
      { id: 'backhoe_tail_lights', label: 'Tail Lights', category: 'General Inspection' },
      { id: 'backhoe_brake_lights', label: 'Brake Lights', category: 'General Inspection' },
      { id: 'backhoe_turn_signals', label: 'Turn Signals', category: 'General Inspection' },
      { id: 'backhoe_hazard_lights', label: 'Hazard Lights', category: 'General Inspection' },
      { id: 'backhoe_strobes', label: 'Strobes', category: 'General Inspection' },
      { id: 'backhoe_backup_lights', label: 'Back up lights', category: 'General Inspection' },
      { id: 'backhoe_horn', label: 'Horn', category: 'General Inspection' },
      { id: 'backhoe_heater_ac', label: 'Heater/AC', category: 'General Inspection' },
      { id: 'backhoe_wipers', label: 'Wipers', category: 'General Inspection' },
      { id: 'backhoe_tires', label: 'Tires', category: 'General Inspection' },
      { id: 'backhoe_park_brake', label: 'Park Brake', category: 'General Inspection' },
      { id: 'backhoe_hydraulic_system', label: 'Hydraulic System', category: 'General Inspection' },
      { id: 'backhoe_guarding', label: 'Guarding', category: 'General Inspection' },
      { id: 'backhoe_seatbelt', label: 'Seatbelt', category: 'General Inspection' },
      { id: 'backhoe_brakes', label: 'Brakes', category: 'General Inspection' },
      { id: 'backhoe_backup_alarm', label: 'Back up Alarm', category: 'General Inspection' },
      { id: 'backhoe_first_aid', label: 'First-aid Kit', category: 'General Inspection' },
      { id: 'backhoe_fire_extinguisher', label: 'Fire Extinguisher (Has it been Certified in last year?)', category: 'General Inspection' }
    ],
    'Gas Powered General Eqpt': [
      { id: 'gas_trained_certified', label: 'Trained/Certified to use equipment', category: 'General Inspection' },
      { id: 'gas_guards_shields', label: 'Guards, Shields, or Other Safety Devices', category: 'General Inspection' },
      { id: 'gas_operator_controls', label: 'Operator Controls, Levers, Pedals', category: 'General Inspection' },
      { id: 'gas_warning_decals', label: 'Warning Decals or Devices', category: 'General Inspection' },
      { id: 'gas_fuel_oil_level', label: 'Fuel/Oil Level', category: 'General Inspection' },
      { id: 'gas_visible_leaks', label: 'Visible Leaks', category: 'General Inspection' },
      { id: 'gas_exhaust', label: 'Exhaust System', category: 'General Inspection' },
      { id: 'gas_air_filter', label: 'Air Filter', category: 'General Inspection' },
      { id: 'gas_spark_plug', label: 'Spark Plug (if applicable)', category: 'General Inspection' },
      { id: 'gas_engine_cooling', label: 'Engine Cooling System', category: 'General Inspection' },
      { id: 'gas_throttle_controls', label: 'Throttle and Controls Operation', category: 'General Inspection' },
      { id: 'gas_emergency_stops', label: 'Emergency Stops/Shutoffs', category: 'General Inspection' },
      { id: 'gas_starting_system', label: 'Starting System (Pull cord, electric start)', category: 'General Inspection' },
      { id: 'gas_deck_blades', label: 'Deck/Blades (if applicable)', category: 'General Inspection' },
      { id: 'gas_wheels_tires', label: 'Wheels/Tires', category: 'General Inspection' },
      { id: 'gas_handlebar_steering', label: 'Handlebar/Steering Operation', category: 'General Inspection' },
      { id: 'gas_chute_deflector', label: 'Chute/Deflector (if applicable)', category: 'General Inspection' },
      { id: 'gas_first_aid', label: 'First-aid Kit', category: 'General Inspection' },
      { id: 'gas_fire_extinguisher', label: 'Fire Extinguisher (Has it been Certified in last year?)', category: 'General Inspection' }
    ],
    'Genie Dynapac': [
      { id: 'genie_trained_certified', label: 'Trained/Certified to use equipment', category: 'General Inspection' },
      { id: 'genie_platform_condition', label: 'Platform/Basket Condition', category: 'General Inspection' },
      { id: 'genie_guardrails', label: 'Guardrails and Gates', category: 'General Inspection' },
      { id: 'genie_controls', label: 'Platform and Ground Controls', category: 'General Inspection' },
      { id: 'genie_emergency_controls', label: 'Emergency Lowering Controls', category: 'General Inspection' },
      { id: 'genie_safety_harness', label: 'Safety Harness and Lanyard', category: 'General Inspection' },
      { id: 'genie_outriggers', label: 'Outriggers/Stabilizers', category: 'General Inspection' },
      { id: 'genie_boom_condition', label: 'Boom/Lift Mechanism', category: 'General Inspection' },
      { id: 'genie_hydraulic_system', label: 'Hydraulic System', category: 'General Inspection' },
      { id: 'genie_engine_operation', label: 'Engine Operation', category: 'General Inspection' },
      { id: 'genie_fuel_level', label: 'Fuel Level', category: 'General Inspection' },
      { id: 'genie_visible_leaks', label: 'Visible Leaks', category: 'General Inspection' },
      { id: 'genie_exhaust', label: 'Exhaust System', category: 'General Inspection' },
      { id: 'genie_tires_wheels', label: 'Tires/Wheels', category: 'General Inspection' },
      { id: 'genie_warning_lights', label: 'Warning Lights and Alarms', category: 'General Inspection' },
      { id: 'genie_load_capacity', label: 'Load Capacity Chart Visible', category: 'General Inspection' },
      { id: 'genie_inspection_decal', label: 'Current Inspection Decal', category: 'General Inspection' },
      { id: 'genie_operator_manual', label: 'Operator Manual Available', category: 'General Inspection' },
      { id: 'genie_first_aid', label: 'First-aid Kit', category: 'General Inspection' },
      { id: 'genie_fire_extinguisher', label: 'Fire Extinguisher (Has it been Certified in last year?)', category: 'General Inspection' }
    ],
    'Skidsteer': [
      { id: 'skidsteer_trained_certified', label: 'Trained/Certified to use equipment', category: 'General Inspection' },
      { id: 'skidsteer_cab_components', label: 'Cab Components', category: 'General Inspection' },
      { id: 'skidsteer_cab_structure', label: 'Cab Structure (ROPS)', category: 'General Inspection' },
      { id: 'skidsteer_mirrors', label: 'Mirrors', category: 'General Inspection' },
      { id: 'skidsteer_handholds', label: 'Handholds (cab & Frame)', category: 'General Inspection' },
      { id: 'skidsteer_steps', label: 'Steps', category: 'General Inspection' },
      { id: 'skidsteer_fenders', label: 'Fenders/Guarding', category: 'General Inspection' },
      { id: 'skidsteer_fan_shroud', label: 'Fan Shroud & Guards', category: 'General Inspection' },
      { id: 'skidsteer_bucket_condition', label: 'Bucket/Attachment Condition', category: 'General Inspection' },
      { id: 'skidsteer_quick_connect', label: 'Quick Connect System', category: 'General Inspection' },
      { id: 'skidsteer_steering', label: 'Steering Controls', category: 'General Inspection' },
      { id: 'skidsteer_fuel_level', label: 'Fuel Level', category: 'General Inspection' },
      { id: 'skidsteer_visible_leaks', label: 'Visible Leaks', category: 'General Inspection' },
      { id: 'skidsteer_exhaust', label: 'Exhaust System', category: 'General Inspection' },
      { id: 'skidsteer_engine_oil', label: 'Engine Oil Level', category: 'General Inspection' },
      { id: 'skidsteer_coolant', label: 'Coolant Level', category: 'General Inspection' },
      { id: 'skidsteer_hydraulic_level', label: 'Hydraulic Level', category: 'General Inspection' },
      { id: 'skidsteer_warning_lights', label: 'Are there any Warning Lights', category: 'General Inspection' },
      { id: 'skidsteer_headlights', label: 'Headlights', category: 'General Inspection' },
      { id: 'skidsteer_strobes', label: 'Strobes', category: 'General Inspection' },
      { id: 'skidsteer_horn', label: 'Horn', category: 'General Inspection' },
      { id: 'skidsteer_heater_ac', label: 'Heater/AC', category: 'General Inspection' },
      { id: 'skidsteer_tracks_tires', label: 'Tracks/Tires', category: 'General Inspection' },
      { id: 'skidsteer_park_brake', label: 'Park Brake', category: 'General Inspection' },
      { id: 'skidsteer_hydraulic_system', label: 'Hydraulic System', category: 'General Inspection' },
      { id: 'skidsteer_guarding', label: 'Guarding', category: 'General Inspection' },
      { id: 'skidsteer_seatbelt', label: 'Seatbelt', category: 'General Inspection' },
      { id: 'skidsteer_backup_alarm', label: 'Back up Alarm', category: 'General Inspection' },
      { id: 'skidsteer_first_aid', label: 'First-aid Kit', category: 'General Inspection' },
      { id: 'skidsteer_fire_extinguisher', label: 'Fire Extinguisher (Has it been Certified in last year?)', category: 'General Inspection' }
    ],
    'Toro': [
      { id: 'toro_trained_certified', label: 'Trained/Certified to use equipment', category: 'General Inspection' },
      { id: 'toro_guards_shields', label: 'Guards, Shields, or Other Safety Devices', category: 'General Inspection' },
      { id: 'toro_operator_controls', label: 'Operator Controls, Levers, Pedals', category: 'General Inspection' },
      { id: 'toro_warning_decals', label: 'Warning Decals or Devices', category: 'General Inspection' },
      { id: 'toro_fuel_oil_level', label: 'Fuel/Oil Level', category: 'General Inspection' },
      { id: 'toro_visible_leaks', label: 'Visible Leaks', category: 'General Inspection' },
      { id: 'toro_exhaust', label: 'Exhaust System', category: 'General Inspection' },
      { id: 'toro_air_filter', label: 'Air Filter', category: 'General Inspection' },
      { id: 'toro_spark_plug', label: 'Spark Plug (if applicable)', category: 'General Inspection' },
      { id: 'toro_engine_cooling', label: 'Engine Cooling System', category: 'General Inspection' },
      { id: 'toro_throttle_controls', label: 'Throttle and Controls Operation', category: 'General Inspection' },
      { id: 'toro_emergency_stops', label: 'Emergency Stops/Shutoffs', category: 'General Inspection' },
      { id: 'toro_starting_system', label: 'Starting System (Pull cord, electric start)', category: 'General Inspection' },
      { id: 'toro_cutting_deck', label: 'Cutting Deck/Blades', category: 'General Inspection' },
      { id: 'toro_wheels_tires', label: 'Wheels/Tires', category: 'General Inspection' },
      { id: 'toro_steering', label: 'Steering Operation', category: 'General Inspection' },
      { id: 'toro_discharge_chute', label: 'Discharge Chute/Deflector', category: 'General Inspection' },
      { id: 'toro_collection_system', label: 'Collection System (if applicable)', category: 'General Inspection' },
      { id: 'toro_first_aid', label: 'First-aid Kit', category: 'General Inspection' },
      { id: 'toro_fire_extinguisher', label: 'Fire Extinguisher (Has it been Certified in last year?)', category: 'General Inspection' } 
    ],
    'Street Sweeper': [
      { id: 'sweeper_trained_certified', label: 'Trained/Certified to use equipment', category: 'General Inspection' },
      { id: 'sweeper_cab_components', label: 'Cab Components', category: 'General Inspection' },
      { id: 'sweeper_cab_structure', label: 'Cab Structure (ROPS)', category: 'General Inspection' },
      { id: 'sweeper_mirrors', label: 'Mirrors', category: 'General Inspection' },
      { id: 'sweeper_handholds', label: 'Handholds (cab & Frame)', category: 'General Inspection' },
      { id: 'sweeper_steps', label: 'Steps', category: 'General Inspection' },
      { id: 'sweeper_brushes', label: 'Sweeping Brushes Condition', category: 'General Inspection' },
      { id: 'sweeper_water_system', label: 'Water Spray System', category: 'General Inspection' },
      { id: 'sweeper_hopper', label: 'Debris Hopper', category: 'General Inspection' },
      { id: 'sweeper_hydraulics', label: 'Hydraulic System', category: 'General Inspection' },
      { id: 'sweeper_steering', label: 'Steering', category: 'General Inspection' },
      { id: 'sweeper_fuel_level', label: 'Fuel Level', category: 'General Inspection' },
      { id: 'sweeper_visible_leaks', label: 'Visible Leaks', category: 'General Inspection' },
      { id: 'sweeper_exhaust', label: 'Exhaust System', category: 'General Inspection' },
      { id: 'sweeper_engine_oil', label: 'Engine Oil Level', category: 'General Inspection' },
      { id: 'sweeper_coolant', label: 'Coolant Level', category: 'General Inspection' },
      { id: 'sweeper_warning_lights', label: 'Are there any Warning Lights', category: 'General Inspection' },
      { id: 'sweeper_headlights', label: 'Headlights', category: 'General Inspection' },
      { id: 'sweeper_strobes', label: 'Strobes', category: 'General Inspection' },
      { id: 'sweeper_horn', label: 'Horn', category: 'General Inspection' },
      { id: 'sweeper_heater_ac', label: 'Heater/AC', category: 'General Inspection' },
      { id: 'sweeper_tires', label: 'Tires', category: 'General Inspection' },
      { id: 'sweeper_park_brake', label: 'Park Brake', category: 'General Inspection' },
      { id: 'sweeper_seatbelt', label: 'Seatbelt', category: 'General Inspection' },
      { id: 'sweeper_backup_alarm', label: 'Back up Alarm', category: 'General Inspection' },
      { id: 'sweeper_first_aid', label: 'First-aid Kit', category: 'General Inspection' },
      { id: 'sweeper_fire_extinguisher', label: 'Fire Extinguisher (Has it been Certified in last year?)', category: 'General Inspection' }
    ],
    'Toolcat': [
      { id: 'toolcat_trained_certified', label: 'Trained/Certified to use equipment', category: 'General Inspection' },
      { id: 'toolcat_cab_components', label: 'Cab Components', category: 'General Inspection' },
      { id: 'toolcat_cab_structure', label: 'Cab Structure (ROPS)', category: 'General Inspection' },
      { id: 'toolcat_mirrors', label: 'Mirrors', category: 'General Inspection' },
      { id: 'toolcat_handholds', label: 'Handholds (cab & Frame)', category: 'General Inspection' },
      { id: 'toolcat_steps', label: 'Steps', category: 'General Inspection' },
      { id: 'toolcat_fenders', label: 'Fenders/Guarding', category: 'General Inspection' },
      { id: 'toolcat_fan_shroud', label: 'Fan Shroud & Guards', category: 'General Inspection' },
      { id: 'toolcat_attachment', label: 'Attachment/Implement Condition', category: 'General Inspection' },
      { id: 'toolcat_quick_connect', label: 'Quick Connect System', category: 'General Inspection' },
      { id: 'toolcat_steering', label: 'Steering Controls', category: 'General Inspection' },
      { id: 'toolcat_fuel_level', label: 'Fuel Level', category: 'General Inspection' },
      { id: 'toolcat_visible_leaks', label: 'Visible Leaks', category: 'General Inspection' },
      { id: 'toolcat_exhaust', label: 'Exhaust System', category: 'General Inspection' },
      { id: 'toolcat_engine_oil', label: 'Engine Oil Level', category: 'General Inspection' },
      { id: 'toolcat_coolant', label: 'Coolant Level', category: 'General Inspection' },
      { id: 'toolcat_hydraulic_level', label: 'Hydraulic Level', category: 'General Inspection' },
      { id: 'toolcat_warning_lights', label: 'Are there any Warning Lights', category: 'General Inspection' },
      { id: 'toolcat_headlights', label: 'Headlights', category: 'General Inspection' },
      { id: 'toolcat_strobes', label: 'Strobes', category: 'General Inspection' },
      { id: 'toolcat_horn', label: 'Horn', category: 'General Inspection' },
      { id: 'toolcat_heater_ac', label: 'Heater/AC', category: 'General Inspection' },
      { id: 'toolcat_tracks_tires', label: 'Tracks/Tires', category: 'General Inspection' },
      { id: 'toolcat_park_brake', label: 'Park Brake', category: 'General Inspection' },
      { id: 'toolcat_hydraulic_system', label: 'Hydraulic System', category: 'General Inspection' },
      { id: 'toolcat_guarding', label: 'Guarding', category: 'General Inspection' },
      { id: 'toolcat_seatbelt', label: 'Seatbelt', category: 'General Inspection' },
      { id: 'toolcat_backup_alarm', label: 'Back up Alarm', category: 'General Inspection' },
      { id: 'toolcat_first_aid', label: 'First-aid Kit', category: 'General Inspection' },
      { id: 'toolcat_fire_extinguisher', label: 'Fire Extinguisher (Has it been Certified in last year?)', category: 'General Inspection' }
    ],
    'Ice Resurfacer': [
      { id: 'ice_trained_certified', label: 'Trained/Certified to use equipment', category: 'General Inspection' },
      { id: 'ice_cab_components', label: 'Cab Components', category: 'General Inspection' },
      { id: 'ice_cab_structure', label: 'Cab Structure', category: 'General Inspection' },
      { id: 'ice_mirrors', label: 'Mirrors', category: 'General Inspection' },
      { id: 'ice_handholds', label: 'Handholds', category: 'General Inspection' },
      { id: 'ice_steps', label: 'Steps', category: 'General Inspection' },
      { id: 'ice_blade_condition', label: 'Ice Blade Condition', category: 'General Inspection' },
      { id: 'ice_water_system', label: 'Water Distribution System', category: 'General Inspection' },
      { id: 'ice_water_tank', label: 'Water Tank Level and Condition', category: 'General Inspection' },
      { id: 'ice_hydraulics', label: 'Hydraulic System', category: 'General Inspection' },
      { id: 'ice_steering', label: 'Steering', category: 'General Inspection' },
      { id: 'ice_fuel_level', label: 'Fuel Level', category: 'General Inspection' },
      { id: 'ice_visible_leaks', label: 'Visible Leaks', category: 'General Inspection' },
      { id: 'ice_exhaust', label: 'Exhaust System', category: 'General Inspection' },
      { id: 'ice_engine_oil', label: 'Engine Oil Level', category: 'General Inspection' },
      { id: 'ice_coolant', label: 'Coolant Level', category: 'General Inspection' },
      { id: 'ice_warning_lights', label: 'Are there any Warning Lights', category: 'General Inspection' },
      { id: 'ice_headlights', label: 'Headlights', category: 'General Inspection' },
      { id: 'ice_strobes', label: 'Strobes/Warning Lights', category: 'General Inspection' },
      { id: 'ice_horn', label: 'Horn', category: 'General Inspection' },
      { id: 'ice_heater', label: 'Heater', category: 'General Inspection' },
      { id: 'ice_tires', label: 'Tires', category: 'General Inspection' },
      { id: 'ice_park_brake', label: 'Park Brake', category: 'General Inspection' },
      { id: 'ice_seatbelt', label: 'Seatbelt', category: 'General Inspection' },
      { id: 'ice_backup_alarm', label: 'Back up Alarm', category: 'General Inspection' },
      { id: 'ice_first_aid', label: 'First-aid Kit', category: 'General Inspection' },
      { id: 'ice_fire_extinguisher', label: 'Fire Extinguisher (Has it been Certified in last year?)', category: 'General Inspection' }
    ]
    };

    const skidsteerChecklist = defaultChecklists['Skidsteer'] || [];
    const toroChecklist = defaultChecklists['Toro'] || [];

    defaultChecklists['Kubota Zero Turn'] = createChecklistVariant({
      baseChecklist: toroChecklist,
      prefix: 'kubota_zero_turn'
    });

    defaultChecklists['Club Car'] = createChecklistVariant({
      baseChecklist: skidsteerChecklist,
      prefix: 'club_car',
      appendedItems: ['Bucket']
    });

    defaultChecklists['Kubota RTV'] = createChecklistVariant({
      baseChecklist: skidsteerChecklist,
      prefix: 'kubota_rtv',
      appendedItems: ['Bucket']
    });

    defaultChecklists['Willie'] = createChecklistVariant({
      baseChecklist: skidsteerChecklist,
      prefix: 'willie',
      labelOverrides: {
        'Mirrors': 'Mirrors and Glass'
      },
      appendedItems: ['Blade', 'Broom', 'Sander', 'Dump Box']
    });

    return defaultChecklists;
  };

  // Get current inspection checklist based on selected equipment type
  const inspectionChecklist = useMemo(() => {
    if (isLoadingChecklists) return [];
    
    const selectedEquipment = formData.vehicleType;
    if (!selectedEquipment || selectedEquipment === 'other') {
      return equipmentChecklists['Default'] || [];
    }
    
    return equipmentChecklists[selectedEquipment] || equipmentChecklists['Default'] || [];
  }, [formData.vehicleType, equipmentChecklists, isLoadingChecklists]);  // Initialize inspection items when checklist or equipment type changes
  useEffect(() => {
    if (inspectionChecklist.length > 0) {
      const items = inspectionChecklist.map(item => ({
        ...item,
        status: 'ok', // 'ok', 'needs-attention', 'critical'
        description: ''
      }));
      setInspectionItems(items);
      // Clear deficiencies when switching equipment types
      setDeficiencies([]);
    }
  }, [inspectionChecklist]);

  // Fetch current user data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let response;
        let lastError;
        for (const url of ['/api/users/me', '/users/me']) {
          try {
            response = await api.get(url);
            break;
          } catch (e) {
            lastError = e;
          }
        }
        if (!response) throw lastError;

        const profile = response?.data?.user || response?.data || {};
        const departmentName = extractDepartmentName(profile);
        const mappedDepartment = mapUserDepartment(departmentName);
        const displayName = extractDisplayName(profile);
        const email = String(profile.email || profile.USERNAME || profile.username || '').trim();

        if (profile && Object.keys(profile).length > 0) {
          
          setCurrentUser({
            name: displayName,
            firstName: profile.firstName || profile.FIRST_NAME || '',
            lastName: profile.lastName || profile.LAST_NAME || '',
            department: mappedDepartment,
            signature: displayName,
            email
          });
          
          // Update form data with user information
          setFormData(prev => ({
            ...prev,
            inspectorName: displayName,
            department: mappedDepartment
          }));
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        // Fallback to auth context user if API fails
        if (user) {
          const fallbackDeptRaw =
            user.department ||
            user.DEPARTMENT ||
            user.departmentName ||
            user.DEPARTMENT_NAME ||
            '';
          const mappedDepartment = mapUserDepartment(String(fallbackDeptRaw || '').trim());
          const fallbackName = extractDisplayName(user);
          
          setCurrentUser({
            name: fallbackName,
            firstName: user.firstName || user.FIRST_NAME || '',
            lastName: user.lastName || user.LAST_NAME || '',
            department: mappedDepartment,
            signature: fallbackName,
            email: String(user.email || user.USERNAME || user.username || '').trim()
          });
          
          setFormData(prev => ({
            ...prev,
            inspectorName: fallbackName,
            department: mappedDepartment
          }));
        }
      }
    };

    fetchCurrentUser();
  }, [user, mapUserDepartment, extractDepartmentName, extractDisplayName]);

  // Ensure formData.department is populated when currentUser becomes available
  useEffect(() => {
    // Debugging: log current user and formData.department when available
    console.log('VehicleInspection: currentUser=', currentUser, 'auth user=', user, 'formData.department=', formData.department, 'departmentTouched=', departmentTouched);

    // If the user hasn't manually edited Department, keep it in sync with the currentUser/auth user
    if (!departmentTouched) {
      if (currentUser && currentUser.department) {
        setFormData(prev => ({ ...prev, department: currentUser.department }));
      } else if (user && user.department) {
        // fallback to auth context user if API user not present
        const mapped = mapUserDepartment(user.department);
        setFormData(prev => ({ ...prev, department: mapped }));
      }
    }
  }, [currentUser, user, formData.department, mapUserDepartment, departmentTouched]);

  // If API fetch hasn't populated currentUser yet, use auth context user as a quick fallback
  useEffect(() => {
      if (!currentUser && user) {
      const mappedDepartment = mapUserDepartment(user.department);
      const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || '';
      setCurrentUser(prev => prev || {
        name,
        firstName: user.firstName,
        lastName: user.lastName,
        department: mappedDepartment,
        signature: name,
        email: user.email || ''
      });
      setFormData(prev => ({ ...prev, inspectorName: prev.inspectorName || name, department: prev.department || mappedDepartment }));
      console.log('VehicleInspection: populated currentUser from auth user fallback', user, 'mappedDepartment=', mappedDepartment);
    }
  }, [user, currentUser, mapUserDepartment]);

  const updateInspectionItem = (itemId, field, value) => {
    setInspectionItems(prev => 
      prev.map(item => 
        item.id === itemId 
          ? { ...item, [field]: value }
          : item
      )
    );
    // If the description for an item changes and it's already listed as a deficiency,
    // keep the deficiencies array in sync so the server/email receives the text.
    if (field === 'description') {
      setDeficiencies(prev => prev.map(d => d.id === itemId ? { ...d, description: value } : d));
    }
  };

  const handleStatusChange = (itemId, status) => {
    updateInspectionItem(itemId, 'status', status);
    
    if (status === 'needs-attention' || status === 'critical') {
      const item = inspectionItems.find(i => i.id === itemId);
      if (item && !deficiencies.find(d => d.id === itemId)) {
        setDeficiencies(prev => [...prev, { id: itemId, label: item.label, description: item.description || '' }]);
      }
    } else {
      setDeficiencies(prev => prev.filter(d => d.id !== itemId));
    }
  };
  const handleSubmit = async () => {
    // Validate required fields before allowing submission
    const newErrors = {};

    // Always required
    if (!formData.vehicleDescription?.trim()) newErrors.vehicleDescription = true;
    if (!formData.plate?.trim()) newErrors.plate = true;

    // Equipment type is conditionally required for specific equipment
    const conditionallyRequiredTypes = [
      'Light Truck Car SUV',
      'Heavy Truck Eqt > 1 Ton',
      'Backhoe',
      'Loader',
      'Grader'
    ];

    if (conditionallyRequiredTypes.includes(formData.vehicleType)) {
      if (!formData.vehicleType) newErrors.vehicleType = true;
    }

    if (!acknowledged) {
      alert('Please acknowledge that the equipment is safe to use before submitting.');
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      alert('Please fill in all required fields.');
      return;
    } else {
      setFieldErrors({});
    }

    const hasDeficiencies = deficiencies.length > 0;
    
    // Prepare submission data with actual user information
    const submissionData = {
      ...formData,
      inspectorName: currentUser?.name || formData.inspectorName,
      signature: currentUser?.signature || '',
      inspectionItems,
      deficiencies,
      hasDeficiencies,
      acknowledged: true,
      submittedAt: new Date().toISOString(),
      submittedBy: currentUser?.name || formData.inspectorName
    };

    try {
  // Submit to the backend API (ticketing system integration)
  // Note: backend routes are mounted under /api on the server, so post to /api/vehicleInspections
  const response = await api.post('/api/vehicleInspections', submissionData);
      if (response.data.success) {
        if (hasDeficiencies) {
          alert(`Inspection completed with ${deficiencies.length} deficiency(ies). Fleet Manager has been notified.`);
        } else {
          alert('Inspection completed successfully. No deficiencies found.');
        }
        // Reset the form for the next inspection
        resetForm();
      } else {
        alert('Failed to submit inspection. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting inspection:', error);
      alert('An error occurred while submitting the inspection. Please try again.');    }
  };

  // Reset the form to initial defaults
  const resetForm = () => {
    setFormData({
      inspectorName: '',
      department: currentUser?.department || '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().split(' ')[0].substring(0, 5),
      shiftStart: '08:00',
      shiftEnd: '16:00',
      vehicleType: '',
      vehicleDescription: '',
      plate: '',
      currentKms: '',
      lastOilChange: '',
      kmsToNextOilChange: '',
      isCommercialDriver: false,
      commercialEndTime: ''
    });
    setInspectionItems([]);
    setDeficiencies([]);
    setAcknowledged(false);
  setFieldErrors({});
    // Keep currentUser intact
  };

  const canEditDepartment = hasCaoDepartmentEditAccess(currentUser, user);

  return (
    <div className="inspection-app">
      <div className="inspection-container">
        {/* Header */}
        <div className="header">
          <h1 className="header-title">Vehicle & Equipment Inspection</h1>
          <div className="header-info">
            <div className="info-item">
              <User className="icon" />
              {currentUser?.name || formData.inspectorName || 'Loading...'}
            </div>
            <div className="info-item">
              <MapPin className="icon" />
              {getDepartmentDisplayName(formData.department)}
            </div>
            <div className="info-item">
              <Clock className="icon" />
              {formData.date} {formData.time}
            </div>
          </div>
        </div>

        <div className="content">
          {/* Basic Information */}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Department</label>
              <select
                value={formData.department}
                disabled={!canEditDepartment}
                onChange={(e) => {
                  setDepartmentTouched(true);
                  setFormData(prev => ({ ...prev, department: e.target.value }));
                }}
                className={`form-input ${canEditDepartment ? '' : 'form-input-readonly'}`}
                title={canEditDepartment ? 'You can change the Department' : 'Department is automatically set from your user profile'}
              >
                <option value="">{formData.department ? getDepartmentDisplayName(formData.department) : 'Loading...'}</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{getDepartmentDisplayName(dept)}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Inspection Time</label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Shift Start</label>
              <input
                type="time"
                value={formData.shiftStart}
                onChange={(e) => setFormData(prev => ({ ...prev, shiftStart: e.target.value }))}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Shift End</label>
              <input
                type="time"
                value={formData.shiftEnd}
                onChange={(e) => setFormData(prev => ({ ...prev, shiftEnd: e.target.value }))}
                className="form-input"
              />
            </div>
          </div>

          {/* Vehicle Information */}
          <div className="section">
            <h3 className="section-title">
              <Truck className="section-icon" />
              Vehicle/Equipment Information
            </h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  Equipment Type
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <select
                  value={formData.vehicleType}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, vehicleType: value }));
                    setFieldErrors(prev => ({ ...prev, vehicleType: false }));
                  }}
                  className={`form-input ${fieldErrors.vehicleType ? 'border-red-500' : ''}`}
                >
                  <option value="">Select Equipment</option>
                  {/* Keep legacy value displayable without offering it as a choice */}
                  {formData.vehicleType && hiddenEquipmentTypes.has(formData.vehicleType) && (
                    <option value={formData.vehicleType} hidden>
                      {formData.vehicleType}
                    </option>
                  )}
                  {availableEquipmentOptions.map((equipment) => (
                    <option key={equipment} value={equipment}>{equipment}</option>
                  ))}
                </select>
                {fieldErrors.vehicleType && (
                  <p className="text-red-600 text-sm mt-1">
                    Equipment type is required for this kind of inspection.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Description/Unit Number
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData.vehicleDescription}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, vehicleDescription: value }));
                    setFieldErrors(prev => ({ ...prev, vehicleDescription: false }));
                  }}
                  className={`form-input ${fieldErrors.vehicleDescription ? 'border-red-500' : ''}`}
                />
                {fieldErrors.vehicleDescription && (
                  <p className="text-red-600 text-sm mt-1">
                    Description/Unit Number is required.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  License Plate
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData.plate}
                  onChange={(e) => {
                    const rawValue = e.target.value || '';
                    const upperValue = rawValue.toUpperCase();
                    setFormData(prev => ({ ...prev, plate: upperValue }));
                    setFieldErrors(prev => ({ ...prev, plate: false }));
                  }}
                  className={`form-input ${fieldErrors.plate ? 'border-red-500' : ''}`}
                />
                {fieldErrors.plate && (
                  <p className="text-red-600 text-sm mt-1">
                    License Plate is required.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Current Kilometers / Hours</label>
                <input
                  type="number"
                  value={formData.currentKms}
                  onChange={(e) => setFormData(prev => ({ ...prev, currentKms: e.target.value }))}
                  className="form-input"
                />
              </div>

              {(formData.department === 'Parks' || formData.department === 'Recreation') && (
                <>
                  <div className="form-group">
                    <label className="form-label">Last Oil Change Date</label>
                    <input
                      type="date"
                      value={formData.lastOilChange}
                      onChange={(e) => setFormData(prev => ({ ...prev, lastOilChange: e.target.value }))}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Kms to Next Oil Change</label>
                    <input
                      type="number"
                      value={formData.kmsToNextOilChange}
                      onChange={(e) => setFormData(prev => ({ ...prev, kmsToNextOilChange: e.target.value }))}
                      className="form-input"
                    />
                  </div>
                </>
              )}
            </div>

            {formData.department === 'Roads' && (
              <div className="commercial-driver-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isCommercialDriver}
                    onChange={(e) => setFormData(prev => ({ ...prev, isCommercialDriver: e.target.checked }))}
                    className="checkbox"
                  />
                  <span>Commercial Driver</span>
                </label>
                {formData.isCommercialDriver && (
                  <>
                    <div className="commercial-time-group">
                      <label className="form-label">End of Driving Time</label>
                      <input
                        type="time"
                        value={formData.commercialEndTime}
                        onChange={(e) => setFormData(prev => ({ ...prev, commercialEndTime: e.target.value }))}
                        className="form-input"
                      />
                    </div>
                    <div className="commercial-driver-details">
                      <p className="commercial-driver-detail-line">
                        Home Terminal: {COMMERCIAL_DRIVER_HOME_TERMINAL}
                      </p>
                      <p className="commercial-driver-detail-line">
                        Jurisdicitional Inspection Type: {COMMERCIAL_DRIVER_INSPECTION_TYPE}
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Inspection Checklist */}
          <div className="section">
            <h3 className="section-title">Inspection Checklist</h3>
            
            {isLoadingChecklists ? (
              <div className="loading-message">
                <p>Loading equipment-specific inspection checklist...</p>
              </div>
            ) : !formData.vehicleType ? (
              <div className="no-equipment-message">
                <p>Please select an equipment type to view the inspection checklist.</p>
              </div>
            ) : inspectionItems.length === 0 ? (
              <div className="no-checklist-message">
                <p>No inspection checklist available for the selected equipment type.</p>
              </div>
            ) : (
              <div className="checklist">
                <div className="checklist-header-info">
                  <p className="equipment-specific-note">
                    <strong>Equipment-specific checklist for:</strong> {formData.vehicleType}
                  </p>
                </div>
                {inspectionItems.map((item) => (
                <div key={item.id} className="checklist-item">
                  <div className="checklist-header">
                    <div className="checklist-info">
                      <h4 className="checklist-label">{item.label}</h4>
                      <span className="category-badge">{item.category}</span>
                    </div>
                    
                    <div className="status-buttons">
                      <button
                        onClick={() => handleStatusChange(item.id, 'ok')}
                        className={`status-btn ${item.status === 'ok' ? 'status-ok active' : 'status-inactive'}`}
                      >
                        ✓ OK
                      </button>
                      <button
                        onClick={() => handleStatusChange(item.id, 'needs-attention')}
                        className={`status-btn ${item.status === 'needs-attention' ? 'status-warning active' : 'status-inactive'}`}
                      >
                        ⚠ Needs Attention
                      </button>
                      <button
                        onClick={() => handleStatusChange(item.id, 'critical')}
                        className={`status-btn ${item.status === 'critical' ? 'status-critical active' : 'status-inactive'}`}
                      >
                        ✕ Critical
                      </button>
                    </div>
                  </div>

                  {(item.status === 'needs-attention' || item.status === 'critical') && (
                    <div className="description-section">
                      <label className="form-label">Description of Issue</label>
                      <textarea
                        value={item.description}
                        onChange={(e) => updateInspectionItem(item.id, 'description', e.target.value)}
                        placeholder="Describe the deficiency..."
                        className="description-textarea"
                        rows="3"
                      />
                    </div>
                  )}
                </div>
              ))}
              </div>
            )}
          </div>

          {/* Deficiencies Summary */}
          {deficiencies.length > 0 && (
            <div className="section">
              <h3 className="section-title deficiencies-title">
                <AlertTriangle className="section-icon" />
                Deficiencies Found ({deficiencies.length})
              </h3>
              <div className="deficiencies-summary">
                <p className="deficiencies-text">
                  The following issues will be automatically reported to the Fleet Manager:
                </p>
                <ul className="deficiencies-list">
                  {deficiencies.map(def => (
                    <li key={def.id}>{def.label}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Acknowledgment and Signature */}
          <div className="section">
            <h3 className="section-title">Final Acknowledgment</h3>
            
            <div className="acknowledgment-section">
              <label className="acknowledgment-label">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="acknowledgment-checkbox"
                />
                <span className="acknowledgment-text">
                  I acknowledge that I have completed this inspection and confirm that the equipment is safe to use. 
                  I understand that any deficiencies noted above have been reported to Fleet Maintenance and 
                  appropriate action will be taken.
                </span>
              </label>              <div className="signature-section">
                <div className="signature-info">
                  <div>
                    <p className="signature-label">Digital Signature:</p>
                    <p className="signature-name">{currentUser?.signature || 'Loading...'}</p>
                  </div>
                  <div className="signature-date">
                    <p>{formData.date}</p>
                    <p>{formData.time}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="submit-section">
            <button
              onClick={handleSubmit}
              disabled={!acknowledged}
              className={`submit-btn ${acknowledged ? 'submit-enabled' : 'submit-disabled'}`}
            >
              <Send className="submit-icon" />
              Submit Inspection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleInspection;